import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_JOB, GET_JOBS_BY_EVENT, GET_MO_DETAILS } from '../../../graph-queries/tar/job';
import { TJob } from '../../../lib/types';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import { useIsFeatureFlagEnabled, useSelectedAsset, useSelectedEvent } from '../../../lib/util';
import { useHasUserAccess, useHasTarAdminAccess } from '../../../lib/util/auth';
import { Button } from '@bphxd/ds-core-react';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { authorCell } from '../../organisms/AspectsTable/util';
import JobForm from '../../organisms/JobForm/JobForm';
import JobFiltersModal from '../../organisms/JobFiltersModal/JobFiltersModal';
import { EMPTY_JOB_FILTERS } from '../../../lib/constants';
import {
  jobsGlobalFilterFn,
  useJobIsolations,
  useJobsWithTrackerStatuses,
  useJobEmocs,
} from './Jobs.util';
import { useBrowserLocationState } from '../../../lib/util/browser';
import { addSpacesBetweenCapitals } from '../../../lib/util';
import KpiSection from '../../organisms/KpiSection/KpiSection';
import {
  CircleCheck16,
  CircleCheck24,
  CircleClose16,
  CircleClose24,
  Link24,
} from '@bphxd/ds-core-react/lib/icons';
import { TARS_AND_OUTAGES_PATH } from '../../../constants';

const MoStatus = React.memo(function MoStatus(props: { moNumber: string }) {
  const { data } = useQuery(GET_MO_DETAILS, {
    variables: { moNumber: props.moNumber },
    skip: !props.moNumber,
  });
  return <>{data?.sapMo.statusFioriDescription || ''}</>;
});

const MoNumber = React.memo(function MoNumber(props: { moNumber: string }) {
  const { data } = useQuery(GET_MO_DETAILS, {
    variables: { moNumber: props.moNumber },
    skip: !props.moNumber,
  });
  const href = data?.sapMo.viewInFiori;
  return href ? (
    <a href={data?.sapMo.viewInFiori || ''} target="_blank" rel="noreferrer">
      {props.moNumber}
    </a>
  ) : (
    <>{props.moNumber}</>
  );
});

const getTargetStatus = (aspect: string): string[] => {
  switch (aspect) {
    case 'workpackStatus':
      return ['Approved for Use'];
    case 'scaffoldStatus':
      return ['Survey Complete', 'Erected'];
    case 'insulationStatus':
      return ['Surveyed', 'Removed', 'Replaced'];
    case 'liftPlanStatus':
      return ['Approved for Use'];
    case 'procedureStatus':
      return ['Approved for Use'];
    case 'scheduleStatus':
      return ['L4'];
    case 'fieldWalkStatus':
      return ['Virtual Complete', 'Field Complete'];
    case 'permitStatus':
      return [
        'Verified',
        'Active',
        'Authorised',
        'Closed',
        'Closing',
        'Expired',
        'Issued',
        'ReAuthorized',
        'ReIssued',
        'Revalidate',
        'SuspendedForEmergency',
        'SuspendedForReauthorization',
        'SuspendedForRevalidation',
        'SuspendedForSanctionToTest',
      ];
    case 'isolationStatus':
      return [
        'WaitingForAuthorization',
        'IsolationInProgress',
        'IsolationInPlace',
        'Requested for STT',
        'RequestedForSanctionToTest',
        'IsolationInPlaceForSanctionToTest',
        'IsolationInPlaceForLongTerm',
        'DeisolationInProgress',
        'Deisolated',
        'PBUModification',
        'Closed',
        'Modification',
        'RequestForReisolation',
      ];
    case 'leakTestPackStatus':
      return ['Approved for Use'];
    case 'gocStatus':
      return ['Ready'];
    case 'lineWalkPackStatus':
      return ['Approved for Use'];
    case 'mocStatus':
      return [
        //NOTE THESE ARE NOT IN SEQUENTIAL ORDER
        'APPROVED',
        'CLOSED - ARCHIVED',
        'PENDING CLOSURE',
        'AUTHORIZED - EXTENSION1 PENDING',
        'AUTHORIZED',
        'AUTHORIZED - EXT1',
        'AUTHORIZED - EXT2',
        'CLOSED',
        'WAITING AUTHORIZATION',
      ];
  }
  return [];
};

const AspectReadinessIcon = React.memo(function AspectReadinessIcon(props: {
  aspect: string;
  status: string;
}) {
  const aspectTargetStatus = getTargetStatus(props.aspect);
  return aspectTargetStatus.includes(props.status) ? (
    <CircleCheck24 title={props.status} color="#15AD5C" />
  ) : props.status === 'Not Required' ? (
    <div title={props.status}> N/A </div>
  ) : props.status === 'Link Required' ? (
    <Link24 title={props.status} color="#E64949" />
  ) : (
    <CircleClose24 title={props.status} className="text-danger" />
  );
});

const JobsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event } = useSelectedEvent();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const selectedAsset = useSelectedAsset();
  const isKpiMetricsEnabled = useIsFeatureFlagEnabled('FEATURE_FLAG_ENABLE_KPI_METRICS');

  const isFiltersEnabled = useIsFeatureFlagEnabled('FEATURE_FLAG_ENABLE_JOBS_FILTER');
  const [filters, setFilters] = useBrowserLocationState('filters', EMPTY_JOB_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (event && selectedAsset && event?.assetId !== selectedAsset?.assetId) {
      navigate(`/${TARS_AND_OUTAGES_PATH}/events?asset=${selectedAsset?.assetName}`, {
        replace: true,
      });
    }
  }, [event, selectedAsset]);

  const { data, loading } = useQuery(GET_JOBS_BY_EVENT, {
    variables: { eventId: event?.eventId || -1 },
    skip: !event?.eventId,
  });
  const [createJob, { loading: isCreating }] = useMutation(CREATE_JOB, {
    refetchQueries: [
      {
        query: GET_JOBS_BY_EVENT,
        variables: { eventId: event?.eventId || -1 },
      },
    ],
    awaitRefetchQueries: true,
  });
  const onAddSubmit = (job: TJob) => {
    const input = _.omit(job, ['jobId', 'updatedAt', 'updatedBy', 'fieldWalk', 'scheduleStatus']);
    input.eventId = event?.eventId || input.eventId;
    return createJob({ variables: { input } }).then((res) => {
      navigate(`/${TARS_AND_OUTAGES_PATH}/jobs/${res.data?.createJob.jobId}${location.search}`);
      return res;
    });
  };

  const jobs = (data?.jobsByEventId as unknown as TJob[]) || [];

  const ecowIsolations = useJobIsolations(jobs);

  const eMocs = useJobEmocs(jobs);

  const jobsWithTrackerStatus = useJobsWithTrackerStatuses(jobs, ecowIsolations, eMocs);

  const isLoading = isCreating || loading;
  return (
    <Layout>
      <Container>
        {isKpiMetricsEnabled && <KpiSection />}
        {isFilterOpen && (
          <JobFiltersModal
            defaultValues={filters}
            onSubmit={setFilters}
            onClose={() => setIsFilterOpen(false)}
          />
        )}
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TJob>
            hideSelectionColumn
            aspectName="job"
            title={`${event?.eventDescription} Overview`}
            data={jobsWithTrackerStatus}
            columns={[
              {
                header: 'Job',
                accessorKey: 'jobId',
                // cell: (context) => {
                //   return (
                //     <div className="d-flex justify-content-center align-items-center">
                //       {context.row.original.jobId}
                //       {context.row.original.jobId !== 3 ? (
                //         <CircleCheck24 color="#15AD5C"></CircleCheck24>
                //       ) : (
                //         <CircleClose24 color="#E64949" />
                //       )}
                //     </div>
                //   );
                // },
              },
              {
                header: 'Description',
                accessorKey: 'jobDescription',
                enableSorting: false,
              },
              {
                header: 'MO Number',
                accessorKey: 'moNumber',
                enableSorting: true,
                cell: (context) => {
                  return <MoNumber moNumber={context.getValue() as string} />;
                },
              },
              {
                header: 'MO Status',
                accessorKey: 'moStatus',
                enableSorting: false,
                cell: (context) => {
                  return <MoStatus moNumber={context.row.original.moNumber} />;
                },
              },
              {
                header: 'Workpack',
                accessorKey: 'aspectStatuses.workpackStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="workpackStatus"
                      status={context.row.original.aspectStatuses?.workpackStatus || ''}
                    />
                  );
                },
              },

              {
                header: 'Scaffold',
                accessorKey: 'aspectStatuses.scaffoldStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="scaffoldStatus"
                      status={context.row.original.aspectStatuses?.scaffoldStatus || ''}
                    />
                  );
                },
              },
              {
                header: 'Liftplan',
                accessorKey: 'aspectStatuses.liftPlanStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="liftPlanStatus"
                      status={context.row.original.aspectStatuses?.liftPlanStatus || ''}
                    />
                  );
                },
              },
              {
                header: 'Insulation',
                accessorKey: 'aspectStatuses.insulationStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="insulationStatus"
                      status={context.row.original.aspectStatuses?.insulationStatus || ''}
                    />
                  );
                },
              },
              {
                header: 'Permit',
                accessorKey: 'aspectStatuses.permitStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="permitStatus"
                      status={context.row.original.aspectStatuses?.permitStatus || ''}
                    />
                  );
                },
              },
              {
                header: 'Isolation',
                accessorKey: 'aspectStatuses.isolationStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="isolationStatus"
                      status={context.row.original.aspectStatuses?.isolationStatus || ''}
                    />
                  );
                },
              },
              /*               {
                header: 'Line Walk Pack',
                accessorKey: 'aspectStatuses.lineWalkPack',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="lineWalkPackStatus"
                      status={context.row.original.aspectStatuses?.li || ''}
                    />
                  );
                },
              }, */
              {
                header: 'MOC',
                accessorKey: 'aspectStatuses.mocStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="mocStatus"
                      status={context.row.original.aspectStatuses?.mocStatus || ''}
                    />
                  );
                },
              },

              /* {
                header: 'Leak Test Pack',
                accessorKey: 'aspectStatuses.leakTestPackStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="leakTestPackStatus"
                      status={context.row.original.aspectStatuses?.leakTestPackStatus || ''}
                    />
                  );
                },
              }, */
              {
                header: 'GoC',
                accessorKey: 'aspectStatuses.gocStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="gocStatus"
                      status={context.row.original.aspectStatuses?.gocStatus || ''}
                    />
                  );
                },
              },
              //{
              // header: 'PO',
              //  accessorKey: 'aspectStatuses.purchaseOrderStatus',
              //},
              // {
              //   header: 'Procedure',
              //   accessorKey: 'aspectStatuses.procedureStatus',
              //   cell: (context) => {
              //     return (
              //       <AspectReadinessIcon
              //         aspect="procedureStatus"
              //         status={context.row.original.aspectStatuses?.procedureStatus || ''}
              //       />
              //     );
              //   },
              // },
              {
                header: 'Schedule',
                accessorKey: 'aspectStatuses.scheduleStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="scheduleStatus"
                      status={context.row.original.aspectStatuses?.scheduleStatus || ''}
                    />
                  );
                },
              },
              {
                header: 'Field Walk',
                accessorKey: 'aspectStatuses.fieldWalkStatus',
                cell: (context) => {
                  return (
                    <AspectReadinessIcon
                      aspect="fieldWalkStatus"
                      status={context.row.original.aspectStatuses?.fieldWalkStatus || ''}
                    />
                  );
                },
              },
              {
                header: 'SPA',
                accessorKey: 'assignedPlanner',
              },
              // {
              //   header: 'Last Updated',
              //   accessorKey: 'updatedAt',
              //   cell: authorCell,
              // },
              {
                header: 'Action',
                accessorKey: 'action',
                enableSorting: false,
                cell: (props) => {
                  const jobIds = props.table.getSortedRowModel().rows.map((r) => r.original.jobId);
                  const row = props.row.original;
                  const title = Object.entries(
                    _.pickBy(
                      _.pick(row.aspectStatuses, [...Object.keys(EMPTY_JOB_FILTERS), 'fieldWalk']),
                      (v) => !!v
                    )
                  )
                    .map(([key, val]) => `${key}: ${val}`)
                    .join('\n');
                  return (
                    <Button
                      size="xs"
                      level="tertiary"
                      onClick={() => {
                        navigate(`/${TARS_AND_OUTAGES_PATH}/jobs/${row.jobId}${location.search}`, {
                          state: { jobIds },
                        });
                      }}
                      title={title}
                    >
                      Details
                    </Button>
                  );
                },
              },
            ]}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search for keywords, MO numbers, or equipment tags..."
            renderAddForm={(onClose) => (
              <JobForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            globalFilterFn={jobsGlobalFilterFn}
            filters={filters}
            onFilterClick={(isFiltersEnabled && (() => setIsFilterOpen(true))) || undefined}
            onUpdateFilters={(newFilters) => setFilters(newFilters)}
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default JobsPage;
